// Screenshots, screen recordings and file attachments.
//
// Capture always goes through getDisplayMedia, so the browser shows its own
// picker and permission prompt — nothing leaves the page without the person
// explicitly choosing what to share. There is no DOM-rasterising fallback on
// purpose: it would need a heavy dependency and would still silently miss
// cross-origin iframes, canvas and video, which is worse than not offering it.

/**
 * Pristine references, taken at module-evaluation time — which happens on
 * import, before init() patches anything.
 *
 * Uploads must not be recorded by our own network capture: a report would
 * otherwise describe its own attachment upload, and a retry would append to a
 * buffer that is already being serialised.
 */
const nativeFetch = typeof window !== 'undefined' && window.fetch
  ? window.fetch.bind(window)
  : null;
const nativeXhrOpen = typeof XMLHttpRequest !== 'undefined'
  ? XMLHttpRequest.prototype.open
  : null;
const nativeXhrSend = typeof XMLHttpRequest !== 'undefined'
  ? XMLHttpRequest.prototype.send
  : null;

/**
 * What the API will presign. Anything else is rejected before we ask.
 *
 * Mirrors UPLOAD_MIMES in the API's storage.service.ts. The two are checked
 * independently — this one to fail early with a message the person can act on,
 * that one because a browser is not a place to enforce anything.
 */
export const UPLOAD_MIMES = [
  'image/png', 'image/jpeg', 'image/webp', 'image/gif', 'video/webm', 'video/mp4',
  'audio/webm', 'audio/ogg', 'audio/mpeg', 'audio/mp4', 'audio/wav',
  'application/pdf', 'text/plain', 'text/csv', 'application/json', 'application/zip',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

/**
 * What a *bug report* may carry, which is narrower than what may be uploaded.
 *
 * A report's attachments are evidence of the bug — what the screen looked like,
 * what the session did — and the API types them SCREENSHOT or VIDEO. A document
 * has no honest value there: filed as a SCREENSHOT it would render as a broken
 * image in the dashboard's media viewer.
 *
 * Documents and voice notes belong on a *message*, where the type is derived
 * from the mime and the reader gets a player or a download.
 */
export const REPORT_MIMES = [
  'image/png', 'image/jpeg', 'image/webp', 'video/webm', 'video/mp4',
];

export const voiceSupported = () =>
  Boolean(navigator?.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined');

export const captureSupported = () =>
  Boolean(navigator?.mediaDevices?.getDisplayMedia && typeof MediaRecorder !== 'undefined');

export const screenshotSupported = () =>
  Boolean(navigator?.mediaDevices?.getDisplayMedia);

/**
 * MediaRecorder reports `video/webm;codecs=vp9`, but the presign signature
 * binds an exact Content-Type and the API only allows the base types. Strip
 * the codec parameter so the type we sign, the header we send and the type the
 * API stored all agree.
 */
export function normalizeMime(mime, allowed = UPLOAD_MIMES) {
  const base = String(mime || '').split(';')[0].trim().toLowerCase();
  return allowed.includes(base) ? base : null;
}

/** Grab a single frame of a chosen screen/window/tab as a PNG blob. */
export async function captureScreenshot() {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { frameRate: 1 },
    audio: false,
  });
  try {
    const track = stream.getVideoTracks()[0];
    const video = document.createElement('video');
    video.srcObject = stream;
    video.muted = true;
    video.playsInline = true;
    await video.play();

    // Wait one frame so the first paint isn't blank.
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    const { width, height } = track.getSettings();
    const canvas = document.createElement('canvas');
    canvas.width = width || video.videoWidth;
    canvas.height = height || video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((res) => canvas.toBlob(res, 'image/png'));
    if (!blob) throw new Error('The browser returned an empty screenshot.');
    return { blob, mime: 'image/png', kind: 'SCREENSHOT' };
  } finally {
    // Always release the capture, including on failure — otherwise the browser
    // keeps showing "sharing your screen".
    stream.getTracks().forEach((t) => t.stop());
  }
}

/**
 * Start recording a chosen screen/window/tab. Returns a handle with stop() and
 * cancel(); the caller decides which. Recording ends on its own if the user
 * clicks the browser's "Stop sharing" button or the cap is reached.
 */
export async function startRecording({ maxMs = 120_000, onTick, onAutoStop } = {}) {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { frameRate: 15 },
    audio: false,
  });

  const mime = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm']
    .find((t) => MediaRecorder.isTypeSupported(t)) || 'video/webm';

  const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 2_500_000 });
  const chunks = [];
  recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
  recorder.start(1000);

  const startedAt = Date.now();
  const timer = setInterval(() => onTick?.(Math.floor((Date.now() - startedAt) / 1000)), 500);

  const cleanup = () => {
    clearInterval(timer);
    clearTimeout(capTimer);
    stream.getTracks().forEach((t) => t.stop());
  };

  const finished = new Promise((resolve) => {
    recorder.onstop = () => {
      cleanup();
      resolve(new Blob(chunks, { type: mime }));
    };
  });

  const endEarly = () => {
    if (recorder.state !== 'inactive') recorder.stop();
    onAutoStop?.();
  };

  // The user can stop sharing from the browser's own UI; treat that as "done".
  stream.getVideoTracks()[0].addEventListener('ended', endEarly);

  // Hard cap so a forgotten recording can't grow without bound.
  const capTimer = setTimeout(endEarly, maxMs);

  return {
    mime,
    maxMs,
    async stop() {
      clearTimeout(capTimer);
      if (recorder.state !== 'inactive') recorder.stop();
      return { blob: await finished, mime, kind: 'VIDEO' };
    },
    cancel() {
      cleanup();
      if (recorder.state !== 'inactive') recorder.stop();
    },
  };
}

/**
 * Record a voice note from the microphone. Returns a handle with stop() and
 * cancel(), like startRecording() above.
 *
 * Audio only, and from getUserMedia rather than getDisplayMedia: this is
 * somebody talking into their laptop to explain a bug, not a capture of the
 * screen. The browser shows its own microphone prompt, so nothing is recorded
 * without an explicit grant.
 *
 * The container is whichever the browser will give us — Chromium and Firefox
 * produce webm/opus, Safari mp4/aac — because MediaRecorder cannot be made to
 * agree on one, and the API allows both.
 */
export async function startVoiceNote({ maxMs = 120_000, onTick, onAutoStop } = {}) {
  const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

  const mime = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg']
    .find((t) => MediaRecorder.isTypeSupported(t)) || '';

  const recorder = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
  const chunks = [];
  recorder.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
  recorder.start(1000);

  const startedAt = Date.now();
  const timer = setInterval(() => onTick?.(Math.floor((Date.now() - startedAt) / 1000)), 500);

  const cleanup = () => {
    clearInterval(timer);
    clearTimeout(capTimer);
    stream.getTracks().forEach((t) => t.stop());
  };

  const finished = new Promise((resolve) => {
    recorder.onstop = () => {
      cleanup();
      resolve(new Blob(chunks, { type: recorder.mimeType || mime }));
    };
  });

  const endEarly = () => {
    if (recorder.state !== 'inactive') recorder.stop();
    onAutoStop?.();
  };

  // A hard cap, as for screen recording: a forgotten open microphone is worse
  // than a truncated note.
  const capTimer = setTimeout(endEarly, maxMs);

  return {
    mime: recorder.mimeType || mime,
    maxMs,
    async stop() {
      clearTimeout(capTimer);
      if (recorder.state !== 'inactive') recorder.stop();
      const blob = await finished;
      return { blob, mime: blob.type || recorder.mimeType || mime, kind: 'AUDIO' };
    },
    cancel() {
      cleanup();
      if (recorder.state !== 'inactive') recorder.stop();
    },
  };
}

/**
 * Ask the API for a presigned URL and PUT the blob straight at it, so media
 * never passes through the API request path.
 *
 * Resolves to the storage key, which is what a report references.
 */
export async function uploadAttachment({ apiBase, apiKey, blob, mime, onProgress }) {
  const type = normalizeMime(mime || blob.type);
  if (!type) {
    throw new Error(`[BugCatch] ${mime || blob.type || 'that file'} can't be attached.`);
  }

  const res = await nativeFetch(`${apiBase}/ingest/presign`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-BugCatch-Key': apiKey },
    body: JSON.stringify({ mime: type }),
  });
  if (!res.ok) throw new Error(`[BugCatch] could not start the upload (HTTP ${res.status}).`);
  const { key, uploadUrl } = await res.json();

  await put(uploadUrl, blob, type, onProgress);
  return { storageKey: key, mime: type };
}

/**
 * XHR rather than fetch purely for upload progress — a recording is megabytes,
 * and a progress bar is the difference between "working" and "frozen".
 */
function put(uploadUrl, blob, mime, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    nativeXhrOpen.call(xhr, 'PUT', uploadUrl, true);
    xhr.setRequestHeader('Content-Type', mime);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress?.(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`[BugCatch] upload failed (HTTP ${xhr.status}).`));
    };
    // The PUT goes to the bucket, not to the API, so a missing bucket CORS
    // policy arrives here as a bare network error with no status. Reporting it
    // as "network error" sends people to read API logs that are perfectly clean.
    xhr.onerror = () => reject(new Error(
      '[BugCatch] the browser blocked the upload to storage. If this is a new '
      + 'environment, the media bucket is probably missing its CORS policy.',
    ));
    xhr.onabort = () => reject(new Error('[BugCatch] upload cancelled.'));
    nativeXhrSend.call(xhr, blob);
  });
}

export function humanSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

export function humanDuration(seconds) {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
